<script setup>
import { reactive, watch, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, minValue, maxValue } from '@vuelidate/validators';
import Swal from 'sweetalert2';

const props = defineProps({
  visible: Boolean,
  modelValue: Object,
  isEdit: Boolean,
  onSave: Function
});
const emit = defineEmits(['update:visible']);

const form = reactive({
  nombre: '',
  modo: 'fijo',
  dias_equivalentes: 30,
  descripcion: '',
});

const isVariableVisitas = computed(() => form.modo === 'variable' || form.modo === 'visitas');
const isIlimitado = computed(() => form.modo === 'ilimitado');
const isFijo = computed(() => form.modo === 'fijo');

const rules = computed(() => ({
  nombre: { required, minLength: minLength(3) },
  modo: { required },
  dias_equivalentes: isFijo.value
    ? { required, minValue: minValue(1), maxValue: maxValue(5000) }
    : {},
  descripcion: {},
}));

const v$ = useVuelidate(rules, form);
const isEdit = computed(() => !!props.modelValue?.id);

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      Object.assign(form, {
        nombre: val.nombre || '',
        modo: val.modo || 'fijo',
        dias_equivalentes: val.dias_equivalentes ?? 30,
        descripcion: val.descripcion || '',
      });
    } else {
      Object.assign(form, { nombre: '', modo: 'fijo', dias_equivalentes: 30, descripcion: '' });
    }
  },
  { immediate: true }
);

watch(
  () => form.modo,
  (modo) => {
    if (modo === 'variable' || modo === 'visitas') form.dias_equivalentes = 0;
    if (modo === 'ilimitado') form.dias_equivalentes = 9999;
    if (modo === 'fijo' && (!form.dias_equivalentes || form.dias_equivalentes < 1)) form.dias_equivalentes = 30;
  }
);

const cerrar = () => {
  emit('update:visible', false);
  v$.value.$reset();
};

const guardar = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  const resultado = await props.onSave({
    ...form,
    ...(props.isEdit ? { id: props.modelValue.id } : {})
  });
  if (resultado === true) {
    await Swal.fire('Éxito', 'Duración guardada correctamente', 'success');
    cerrar();
  } else {
    cerrar();
    await Swal.fire('Advertencia', resultado, 'warning');
  }
};

const modoAyuda = computed(() => {
  if (form.modo === 'fijo') return 'Usa días fijos. Ej: MES 30. Cantidad del plan queda en 1.';
  if (form.modo === 'variable') return 'Duración manual: al crear el plan pedirá cantidad (días). Ej: TEMPORADA.';
  if (form.modo === 'visitas') return 'Sin tiempo: el plan pedirá límite de visitas y fecha_fin será null.';
  return 'Sin vencimiento: fecha_fin null, nunca vence.';
});
</script>

<template>
  <div class="modal fade" tabindex="-1" :class="{ show: visible }" style="display: block;" v-if="visible">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEdit ? 'Editar' : 'Nueva' }} Duración</h5>
          <button type="button" class="btn-close" @click="cerrar"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="guardar">
            <div class="mb-3">
              <label class="form-label">Nombre</label>
              <input v-model="form.nombre" type="text" class="form-control form-control-sm"
                :class="{ 'is-invalid': v$.nombre.$dirty && v$.nombre.$invalid }" @blur="v$.nombre.$touch()" />
              <div class="invalid-feedback" v-if="v$.nombre.$dirty && v$.nombre.$invalid">
                <div v-if="!v$.nombre.required">El nombre es obligatorio.</div>
                <div v-else-if="!v$.nombre.minLength">Debe tener al menos 3 caracteres.</div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Modo</label>
              <select v-model="form.modo" class="form-select form-select-sm">
                <option value="fijo">Fijo (días)</option>
                <option value="variable">Variable (pide cantidad en días)</option>
                <option value="visitas">Por visitas (sin tiempo)</option>
                <option value="ilimitado">Ilimitado (sin vencimiento)</option>
              </select>
              <small class="text-muted">{{ modoAyuda }}</small>
            </div>

            <div class="mb-3">
              <label class="form-label">Días equivalentes</label>
              <input v-model.number="form.dias_equivalentes" type="number" min="0" class="form-control form-control-sm"
                :disabled="isVariableVisitas || isIlimitado"
                :class="{ 'is-invalid': v$.dias_equivalentes.$dirty && v$.dias_equivalentes.$invalid }"
                @blur="v$.dias_equivalentes.$touch()" />
              <small v-if="isVariableVisitas" class="text-muted">Forzado a 0 en este modo.</small>
              <small v-if="isIlimitado" class="text-muted">Forzado a 9999 en este modo.</small>
              <div class="invalid-feedback" v-if="v$.dias_equivalentes.$dirty && v$.dias_equivalentes.$invalid">
                Debe ser entero 1..5000.
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Descripción</label>
              <input v-model="form.descripcion" type="text" class="form-control form-control-sm" />
            </div>

            <div class="text-end">
              <button type="button" class="btn btn-secondary btn-sm me-2" @click="cerrar">Cancelar</button>
              <button type="submit" class="btn btn-primary btn-sm">{{ isEdit ? 'Actualizar' : 'Guardar' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal { background: rgba(0, 0, 0, 0.4); z-index: 9999 !important; }
</style>
